/**
 * The /stock/movements search params and their guard (§4.1).
 *
 * Apart from the page for the same reason W-06's parser is: `validateSearch` runs
 * in the route module the router loads eagerly, so importing it from the page
 * would drag the ledger, its drawer and the form into the initial bundle.
 */
import { LEDGER_PRESETS, type LedgerPreset } from '@aslen/core';

export interface LedgerSearch {
  /** Date chip; "custom" keeps its two days in `from` / `to`. */
  preset?: LedgerPreset;
  from?: string;
  to?: string;
  /** csv of `stock_movement_kind` */
  kind?: string;
  /** csv of warehouse ids — a movement matches on either side */
  warehouse?: string;
  /** csv of user ids */
  actor?: string;
  /** csv of reason code ids */
  reason?: string;
  /** one item (C39: the web addresses items by id) */
  item?: string;
  /** one lot, by its number — the filter W-08's "Hareketleri gör" arrives with */
  lot?: string;
  /** one reference document, by its number — W-09's approved banner links here */
  ref?: string;
  /** "Geri alınanları göster" */
  reversed?: boolean;
  /** `?new=1` opens the movement form */
  new?: boolean;
  /** a movement's document number — opens the read-only detail drawer */
  m?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);
const iso = (v: unknown): string | undefined => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
// the router parses `?new=1` into the number 1 and `?new=true` into a boolean,
// and a link pasted by hand may carry either spelling
const flag = (v: unknown): boolean | undefined => (v === true || v === 1 || v === '1' || v === 'true' ? true : undefined);

export function parseLedgerSearch(s: Record<string, unknown>): LedgerSearch {
  const preset = (LEDGER_PRESETS as readonly string[]).includes(s.preset as string) ? (s.preset as LedgerPreset) : undefined;
  const from = iso(s.from);
  const to = iso(s.to);
  return {
    // a link that carries days but no chip is a custom range
    preset: preset ?? (from || to ? 'custom' : undefined),
    from,
    to,
    kind: str(s.kind),
    warehouse: str(s.warehouse),
    actor: str(s.actor),
    reason: str(s.reason),
    item: str(s.item),
    lot: str(s.lot),
    ref: str(s.ref),
    reversed: flag(s.reversed),
    new: flag(s.new),
    m: str(s.m),
  };
}

export const splitCsv = (v: string | undefined): string[] => (v ? v.split(',').filter(Boolean) : []);
export const joinCsv = (v: string[]): string | undefined => (v.length ? v.join(',') : undefined);
